'use client';

import { useEffect, useState, useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/browser';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { useUserContext } from '@/components/providers/UserProvider';
import {
  ArrowLeft,
  MapPin,
  DollarSign,
  Briefcase,
  Building2,
  Send,
  Bookmark,
  Tag as TagIcon,
  Pencil,
  Trash2,
  CheckCircle,
} from 'lucide-react';
import AuditionModal from '@/components/AuditionModal';
import ShareJobButton from '@/components/ShareJobButton';
import type { AuditionCharacter } from '@/types/jobs';
import { getJobStatus } from '@/lib/jobStatus';
import { Avatar, Button as DsButton, Card, Tag } from '@/components/ds';
import { cn } from '@/lib/utils';

function normalizeCharacters(auditionLines: unknown): AuditionCharacter[] {
  if (!auditionLines) return [];
  if (typeof auditionLines === 'string') {
    try {
      return normalizeCharacters(JSON.parse(auditionLines));
    } catch {
      return [];
    }
  }
  if (!Array.isArray(auditionLines)) return [];
  return auditionLines
    .map((item: unknown) => {
      if (typeof item === 'object' && item !== null && 'id' in item && 'character_name' in item) {
        const o = item as Record<string, unknown>;
        return {
          id: String(o.id),
          character_name: String(o.character_name ?? ''),
          character_description: String(o.character_description ?? ''),
          age_range: String(o.age_range ?? ''),
          sex: String(o.sex ?? ''),
          language: String(o.language ?? ''),
          accent: String(o.accent ?? ''),
          lines: Array.isArray(o.lines) ? (o.lines as string[]) : [],
          allow_video: !!o.allow_video,
        } as AuditionCharacter;
      }
      return null;
    })
    .filter(Boolean) as AuditionCharacter[];
}

export default function JobDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { user } = useAuthContext();
  const { userData } = useUserContext();
  const isAdmin = userData?.profile?.is_admin === true;
  const supabase = createClient();

  const [job, setJob] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hasApplied, setHasApplied] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [submittedCharacterIds, setSubmittedCharacterIds] = useState<Set<string>>(new Set());
  const [auditionModalOpen, setAuditionModalOpen] = useState(false);
  const [auditionModalCharacters, setAuditionModalCharacters] = useState<AuditionCharacter[]>([]);
  const [auditionModalMulti, setAuditionModalMulti] = useState(false);
  const [auditionModalCharacterId, setAuditionModalCharacterId] = useState<string | undefined>(undefined);

  const characters = useMemo(() => normalizeCharacters(job?.audition_lines), [job?.audition_lines]);
  const hasCharacters = characters.length > 0;
  const allowMultiCharacter = !!job?.allow_multi_character;
  const { canApply, statusLabel, tooltip } = getJobStatus(job);

  useEffect(() => {
    loadJob();
    if (user) {
      checkIfApplied();
      checkIfSaved();
    }
  }, [params.jobId, user]);

  const checkIfSaved = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('saved_jobs')
      .select('id')
      .eq('user_id', user.id)
      .eq('job_id', String(params.jobId))
      .maybeSingle();
    setIsSaved(!!data);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!user) {
      router.push('/login?redirect=/jobs/' + params.jobId);
      return;
    }
    setSaving(true);
    try {
      if (isSaved) {
        const { error } = await supabase
          .from('saved_jobs')
          .delete()
          .eq('user_id', user.id)
          .eq('job_id', String(params.jobId));
        if (!error) setIsSaved(false);
      } else {
        const { error } = await supabase
          .from('saved_jobs')
          .insert({ user_id: user.id, job_id: String(params.jobId) });
        if (!error || (error as any).code === '23505') setIsSaved(true);
      }
    } catch (err) {
      console.error('Error saving job:', err);
    } finally {
      setSaving(false);
    }
  };

  const loadJob = async () => {
    try {
      const { data: jobData, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('id', params.jobId)
        .single();

      if (!error && jobData) {
        const posterCol: string | undefined = jobData.posted_by ?? jobData.poster_id ?? undefined;
        let posterData = null;
        if (posterCol) {
          const { data } = await supabase
            .from('profiles')
            .select('id, display_name, username, avatar_url, email')
            .eq('id', posterCol)
            .maybeSingle();
          posterData = data;
        }
        setJob({ ...jobData, poster: posterData, posted_by: posterCol });
      } else {
        setJob(null);
      }
    } catch (error) {
      console.error('Error loading job:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  const checkIfApplied = async () => {
    if (!user?.id) return;
    try {
      const { data: rows, error } = await supabase
        .from('job_applications')
        .select('id, character_id')
        .eq('job_id', params.jobId)
        .eq('applicant_id', user.id);

      if (error) throw error;

      const legacy = rows?.some((r: any) => r.character_id == null);
      setHasApplied(!!legacy);
      const idList = (rows || [])
        .map((r: { character_id?: string }) => r.character_id)
        .filter(Boolean) as string[];
      setSubmittedCharacterIds(new Set(idList));
    } catch {
      try {
        const { data: rows } = await supabase
          .from('job_applications')
          .select('id')
          .eq('job_id', params.jobId)
          .eq('applicant_id', user.id);
        if (rows?.length) setHasApplied(true);
      } catch (err) {
        console.error('Error checking application status:', err);
      }
    }
  };

  const openAuditionForCharacter = (char: AuditionCharacter) => {
    if (!user) {
      router.push('/login?redirect=/jobs/' + params.jobId);
      return;
    }
    setAuditionModalCharacters([char]);
    setAuditionModalMulti(false);
    setAuditionModalCharacterId(char.id);
    setAuditionModalOpen(true);
  };

  const openAuditionForMultiple = () => {
    setAuditionModalCharacters(characters);
    setAuditionModalMulti(true);
    setAuditionModalCharacterId(undefined);
    setAuditionModalOpen(true);
  };

  const openAuditionForGeneral = () => {
    if (!user) {
      router.push('/login?redirect=/jobs/' + params.jobId);
      return;
    }
    const generalChar: AuditionCharacter = {
      id: 'general',
      character_name: 'General Submission',
      character_description: '',
      age_range: '',
      sex: '',
      language: '',
      accent: '',
      lines: [],
      allow_video: false,
    };
    setAuditionModalCharacters([generalChar]);
    setAuditionModalMulti(false);
    setAuditionModalCharacterId('general');
    console.log('[AuditionModal] Opening with jobId:', params.jobId, 'characterId: general');
    setAuditionModalOpen(true);
  };

  const handleRemoveJob = async () => {
    const { error } = await supabase
      .from('job_postings')
      .update({ is_archived: true, status: 'rejected' })
      .eq('id', String(params.jobId));
    if (!error) router.push('/jobs?removed=true');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-text-primary mb-2">Job Not Found</h1>
          <p className="text-text-tertiary text-sm mb-6">
            This job may have been removed or never existed.
          </p>
          <DsButton variant="ghost" size="md" onClick={() => router.push('/jobs')}>
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            Back to Jobs
          </DsButton>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0 py-6">
      <div className="max-w-5xl mx-auto px-4">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => router.push('/jobs')}
          className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          Back to Jobs
        </button>

        {/* Job Header Card */}
        <Card className="mb-6 p-6">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div className="flex items-start gap-4 min-w-0">
              <div className="w-16 h-16 rounded-md bg-accent-soft flex items-center justify-center flex-shrink-0">
                <Building2 className="w-7 h-7 text-accent" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <h1 className="text-[22px] font-semibold tracking-tight text-text-primary mb-1 leading-tight">
                  {job.title}
                </h1>
                {job.company && (
                  <p className="text-text-secondary text-sm flex items-center gap-1.5 mb-2">
                    <Building2 className="w-4 h-4" strokeWidth={1.75} />
                    {job.company}
                  </p>
                )}
                <Tag variant={job.rate ? 'accent' : 'default'}>
                  {job.rate ? 'Paid' : 'Unpaid'}
                </Tag>
                <div className="flex flex-wrap gap-3 mt-3 text-text-secondary text-xs">
                  {job.location && (
                    <div className="inline-flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" strokeWidth={1.75} />
                      <span>{job.location}</span>
                    </div>
                  )}
                  {job.rate && (
                    <div className="inline-flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4" strokeWidth={1.75} />
                      <span className="text-text-primary font-medium tabular-nums">{job.rate}</span>
                    </div>
                  )}
                  {job.job_type && (
                    <div className="inline-flex items-center gap-1.5">
                      <Briefcase className="w-4 h-4" strokeWidth={1.75} />
                      <span className="capitalize">{job.job_type.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 items-center flex-wrap">
              {user && job.posted_by && user.id === job.posted_by && (
                <Link href={`/jobs/${params.jobId}/edit`}>
                  <DsButton variant="secondary" size="sm">
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Edit
                  </DsButton>
                </Link>
              )}
              {isAdmin && (
                <DsButton
                  variant="danger"
                  size="sm"
                  onClick={() => setShowRemoveConfirm(true)}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Remove Job
                </DsButton>
              )}
              <DsButton
                variant="ghost"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                aria-label={isSaved ? 'Remove from saved' : 'Save for later'}
                className={cn(isSaved && 'text-accent')}
              >
                <Bookmark
                  className="w-4 h-4"
                  strokeWidth={1.75}
                  fill={isSaved ? 'currentColor' : 'none'}
                />
              </DsButton>
              <ShareJobButton jobId={String(params.jobId)} jobTitle={job.title} />
            </div>
          </div>

          {/* Apply / Audition: open modal for jobs with no characters */}
          {!hasCharacters && (
            <>
              {!canApply ? (
                <div
                  className="px-4 py-3 bg-surface-3 border border-border-subtle rounded-sm text-center"
                  title={tooltip ?? undefined}
                >
                  <p className="text-text-secondary text-sm font-medium">
                    {statusLabel ?? 'Not accepting applications'}
                  </p>
                </div>
              ) : submittedCharacterIds.has('general') || hasApplied ? (
                <div className="px-4 py-3 bg-accent-soft border border-accent rounded-sm text-center">
                  <p className="text-accent text-sm font-semibold inline-flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" strokeWidth={1.75} />
                    Application Submitted
                  </p>
                </div>
              ) : (
                <DsButton
                  variant="primary"
                  size="md"
                  onClick={openAuditionForGeneral}
                  className="w-full justify-center"
                >
                  <Send className="w-4 h-4" strokeWidth={1.75} />
                  Apply / Audition
                </DsButton>
              )}
            </>
          )}
        </Card>

        {/* Job Details */}
        <Card className="mb-6 p-6">
          <h2 className="text-base font-semibold text-text-primary mb-3">Job Description</h2>
          <p className="text-text-primary text-sm leading-relaxed whitespace-pre-wrap">
            {job.description}
          </p>

          {/* Character cards and audition lines */}
          {hasCharacters && (
            <>
              <h3 className="text-base font-semibold text-text-primary mt-8 mb-4">
                Characters &amp; Auditions
              </h3>
              <div className="space-y-4">
                {characters.map((char) => {
                  const submitted = submittedCharacterIds.has(char.id);
                  return (
                    <Card key={char.id}>
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                        <h4 className="text-base font-semibold text-text-primary">
                          {char.character_name}
                        </h4>
                        <Tag variant={job.rate ? 'accent' : 'default'}>
                          {job.rate ? 'Paid' : 'Unpaid'}
                        </Tag>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        <Tag>{char.language || 'English'}</Tag>
                        <Tag>{char.accent || 'None / Neutral'}</Tag>
                        {char.age_range && <Tag>{char.age_range}</Tag>}
                        {char.sex && <Tag>{char.sex}</Tag>}
                      </div>
                      {char.character_description && (
                        <p className="text-text-primary text-sm leading-relaxed mb-4 whitespace-pre-wrap">
                          {char.character_description}
                        </p>
                      )}
                      {char.lines && char.lines.length > 0 && (
                        <div className="space-y-1.5 mb-4">
                          {char.lines.map((line, i) => (
                            <div
                              key={i}
                              className="text-text-secondary text-sm py-2 px-3 bg-surface-3 rounded-sm border border-border-subtle"
                            >
                              {line}
                            </div>
                          ))}
                        </div>
                      )}
                      {!canApply ? (
                        <Tag>{statusLabel ?? 'Not accepting applications'}</Tag>
                      ) : user ? (
                        submitted ? (
                          <Tag variant="accent">
                            <CheckCircle className="w-3 h-3" strokeWidth={1.75} />
                            Submitted
                          </Tag>
                        ) : (
                          <DsButton
                            variant="primary"
                            size="sm"
                            onClick={() => openAuditionForCharacter(char)}
                          >
                            <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Audition
                          </DsButton>
                        )
                      ) : (
                        <DsButton
                          variant="primary"
                          size="sm"
                          onClick={() => router.push('/login?redirect=/jobs/' + params.jobId)}
                        >
                          Sign in to audition
                        </DsButton>
                      )}
                    </Card>
                  );
                })}
              </div>
              {allowMultiCharacter && characters.length > 1 && canApply && (
                <div className="mt-6">
                  {user ? (
                    <DsButton variant="primary" size="md" onClick={openAuditionForMultiple}>
                      <Send className="w-4 h-4" strokeWidth={1.75} />
                      Audition for multiple characters
                    </DsButton>
                  ) : (
                    <DsButton
                      variant="primary"
                      size="md"
                      onClick={() => router.push('/login?redirect=/jobs/' + params.jobId)}
                    >
                      Sign in to audition for multiple characters
                    </DsButton>
                  )}
                </div>
              )}
            </>
          )}

          {/* Legacy audition lines (plain list when no character structure) */}
          {!hasCharacters &&
            job.audition_lines &&
            Array.isArray(job.audition_lines) &&
            job.audition_lines.length > 0 && (
              <>
                <h3 className="text-base font-semibold text-text-primary mt-8 mb-4">
                  Audition Lines
                </h3>
                <div className="space-y-1.5">
                  {job.audition_lines.map((line: unknown, i: number) => (
                    <div
                      key={i}
                      className="text-text-secondary text-sm py-2 px-3 bg-surface-3 rounded-sm border border-border-subtle"
                    >
                      {typeof line === 'string'
                        ? line
                        : typeof line === 'object' &&
                          line !== null &&
                          'character_name' in (line as object)
                        ? (line as { character_name?: string }).character_name
                        : String(line)}
                    </div>
                  ))}
                </div>
              </>
            )}

          {/* Timeline */}
          {(job.start_date || job.project_end_date) && (
            <>
              <h3 className="text-base font-semibold text-text-primary mt-8 mb-4">Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {job.start_date && (
                  <div className="p-3 bg-surface-3 rounded-sm border border-border-subtle">
                    <p className="text-text-secondary text-xs mb-1">Start Date</p>
                    <p className="text-text-primary text-sm font-medium">
                      {new Date(job.start_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {job.project_end_date && (
                  <div className="p-3 bg-surface-3 rounded-sm border border-border-subtle">
                    <p className="text-text-secondary text-xs mb-1">End Date</p>
                    <p className="text-text-primary text-sm font-medium">
                      {new Date(job.project_end_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {job.estimated_duration && (
                  <div className="p-3 bg-surface-3 rounded-sm border border-border-subtle">
                    <p className="text-text-secondary text-xs mb-1">Duration</p>
                    <p className="text-text-primary text-sm font-medium">
                      {job.estimated_duration}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Tags */}
          {job.tags && job.tags.length > 0 && (
            <>
              <h3 className="text-base font-semibold text-text-primary mt-8 mb-4">
                Skills &amp; Tags
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {job.tags.map((tag: string) => (
                  <Tag key={tag} variant="accent">
                    <TagIcon className="w-3 h-3" strokeWidth={1.75} />
                    {tag}
                  </Tag>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Posted By */}
        <Card className="p-6">
          <h3 className="text-base font-semibold text-text-primary mb-4">Posted By</h3>
          <div className="flex items-center gap-3">
            <Avatar
              src={job.poster?.avatar_url || undefined}
              name={job.poster?.display_name || job.poster?.username || 'User'}
              size={48}
            />
            <div className="min-w-0">
              {job.poster?.username ? (
                <Link
                  href={`/u/${job.poster.username}`}
                  className="text-text-primary text-base font-semibold hover:text-accent transition-colors truncate block"
                >
                  {job.poster?.display_name || job.poster?.username}
                </Link>
              ) : (
                <h4 className="text-text-primary text-base font-semibold truncate">
                  {job.poster?.display_name || 'User'}
                </h4>
              )}
              {job.poster?.username && (
                <p className="text-text-secondary text-xs">@{job.poster.username}</p>
              )}
              {job.poster?.email && (
                <p className="text-text-tertiary text-xs">{job.poster.email}</p>
              )}
            </div>
            {job.poster?.username && (
              <Link href={`/u/${job.poster.username}`} className="ml-auto">
                <DsButton variant="ghost" size="sm">
                  View Profile
                </DsButton>
              </Link>
            )}
          </div>
        </Card>
      </div>

      <AuditionModal
        open={auditionModalOpen}
        onClose={() => setAuditionModalOpen(false)}
        jobId={String(params.jobId)}
        jobTitle={job.title}
        characters={auditionModalCharacters}
        initialCharacterId={auditionModalCharacterId}
        allowMultiCharacter={auditionModalMulti}
        submittedCharacterIds={submittedCharacterIds}
        onSuccess={(ids) => {
          setSubmittedCharacterIds((prev) => new Set([...prev, ...ids]));
          setAuditionModalOpen(false);
        }}
      />

      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-0/80 backdrop-blur-sm">
          <div className="bg-surface-2 border border-border-subtle rounded-md p-6 max-w-sm w-full mx-4">
            <h3 className="text-text-primary text-base font-semibold mb-2">Remove this job?</h3>
            <p className="text-text-secondary text-sm mb-6">
              This will immediately remove the job from the public listing. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <DsButton
                variant="secondary"
                size="md"
                onClick={() => setShowRemoveConfirm(false)}
                className="flex-1 justify-center"
              >
                Cancel
              </DsButton>
              <DsButton
                variant="danger"
                size="md"
                onClick={handleRemoveJob}
                className="flex-1 justify-center"
              >
                Remove
              </DsButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
